import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Alert, Button, Checkbox, Form, Input, Label, Select } from '@/shared/components';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { isApiError, isNotFoundError } from '@/services/api';
import type { Product } from '@/features/products/types/products.types';
import { resolveProductCampaign } from '../services/campaign';
import { useCampaignCancellationRules } from '../hooks/useCampaignCancellationRules';
import { useCancellationFieldMetas } from '../hooks/useCancellationFieldMetas';
import { EMPTY_CANCELLATION_SCHEMA, useCancellationSchemaSync } from '../hooks/useCancellationSchemaSync';
import {
  CANCELLATIONS_ALLOWED_KEY,
  settingsToCancellationForm,
  cancellationFormToSettings,
  toBool,
} from '../services/cancellationRules';
import type { CancellationFieldMeta } from '../services/cancellationRules';
import { DerivedAdjustmentTypes } from './DerivedAdjustmentTypes';
import type { CancellationRulesFormValues, CancellationRulesSchema } from '../schemas/cancellationRules.schema';

interface Props {
  productId: string;
  product: Product;
}

const resolveCampaignKeys = {
  forProduct: (productId: string) => ['smr-admin', 'campaign', 'resolve', productId] as const,
};

const DEFAULT_VALUES: CancellationRulesFormValues = {
  cancellations_allowed: false,
  fields: {},
};

const CANCELLATION_FIELDS_REGION_ID = 'cancellation_fields';

/** Tiny local title-case helper for enum labels. e.g. 'pro_rata' -> 'Pro Rata'. */
const humanise = (value: string) =>
  value
    .split('_')
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

/** Cancellation Rules tab body. Query state, the catalogue partition and the adjustment-type
 * lookup live in useCancellationFieldMetas; this shell owns the form and the render. */
export function CampaignCancellationsPanel({ productId, product }: Props) {
  const resolveQuery = useQuery({
    queryKey: resolveCampaignKeys.forProduct(productId),
    queryFn: () => resolveProductCampaign(productId),
  });

  const campaignId = resolveQuery.data?.campaign_id;
  const { query: rulesQuery, saveMutation } = useCampaignCancellationRules(campaignId);

  const { hasPermission } = usePermissions();
  const canEdit = hasPermission({ resource: 'campaign', action: 'edit' });
  const isRetired = Boolean(product.deleted_at);
  const controlsDisabled = isRetired || !canEdit;

  // The resolver reads this ref rather than closing over a schema variable directly: the schema
  // needs formFieldMetas from the hook below, which itself needs `watch` from this call.
  const schemaRef = useRef<CancellationRulesSchema>(EMPTY_CANCELLATION_SCHEMA);
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { isDirty, isSubmitted, isSubmitting },
  } = useForm<CancellationRulesFormValues>({
    resolver: (values, context, options) => zodResolver(schemaRef.current)(values, context, options),
    defaultValues: DEFAULT_VALUES,
  });

  const noCustomerRefund = toBool(watch('fields.no_customer_refund'));
  const {
    formFieldMetas,
    derivedFieldMetas,
    masterConfigOptionId,
    adjustment,
    isCatalogLoading,
    isCatalogError,
  } = useCancellationFieldMetas(product.parent_dealer_id, rulesQuery.data, noCustomerRefund);

  const { fieldKeysSignature } = useCancellationSchemaSync({
    formFieldMetas,
    schemaRef,
    isSubmitted,
    revalidate: trigger,
  });

  // No `formFieldMetas.length === 0` early-return: a stored master toggle must still seed even
  // on an empty catalogue. isDirty guards against a background refetch clobbering an edit.
  useEffect(() => {
    if (!rulesQuery.data) return;
    if (isDirty) return;
    reset(settingsToCancellationForm(rulesQuery.data, formFieldMetas));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesQuery.data, fieldKeysSignature]);

  const enabled = watch('cancellations_allowed');

  const onSubmit = (values: CancellationRulesFormValues) => {
    // Pass the master id alongside formFieldMetas so cancellationFormToSettings can attach it —
    // deriveFieldMetas deliberately excludes the master key from formFieldMetas.
    const metasWithMaster: CancellationFieldMeta[] = masterConfigOptionId
      ? [
          ...formFieldMetas,
          {
            key: CANCELLATIONS_ALLOWED_KEY,
            label: 'Cancellations Allowed',
            control: 'checkbox',
            configOptionId: masterConfigOptionId,
          },
        ]
      : formFieldMetas;
    saveMutation.mutate(cancellationFormToSettings(values, metasWithMaster), {
      // Re-seed from the server-normalized response so the form is not left dirty and the
      // background-refetch guard above can re-seed on subsequent refetches (PR #652 lesson).
      onSuccess: (saved) => reset(settingsToCancellationForm(saved, formFieldMetas)),
    });
  };

  if (resolveQuery.isLoading) {
    return <p className="text-sm text-mp-muted">Loading cancellation rules…</p>;
  }

  const noCampaignAlert = (
    <Alert variant="warning">
      <span>This product has no campaign yet. Cancellation rules are unavailable until one is provisioned.</span>
    </Alert>
  );

  // An unprovisioned product's resolver 404s; every other resolver error falls through below.
  if (isNotFoundError(resolveQuery.error)) {
    return noCampaignAlert;
  }

  if (resolveQuery.isError) {
    return (
      <Alert variant="error">
        <span>Failed to load this product&apos;s campaign. Cancellation rules are unavailable.</span>
      </Alert>
    );
  }

  // Belt-and-braces: also cover a 200-with-empty-campaign_id shape.
  if (!campaignId) {
    return noCampaignAlert;
  }

  if (rulesQuery.isLoading) {
    return <p className="text-sm text-mp-muted">Loading cancellation rules…</p>;
  }

  if (rulesQuery.isError) {
    return (
      <Alert variant="error">
        <span>Failed to load cancellation rules.</span>
      </Alert>
    );
  }

  // The catalogue gets the same explicit loading/error branches as the two queries above, so
  // an in-flight or failed catalogue is never mistaken for a genuinely empty one.
  if (isCatalogLoading) {
    return <p className="text-sm text-mp-muted">Loading the cancellation catalogue…</p>;
  }

  if (isCatalogError) {
    return (
      <Alert variant="error">
        <span>Failed to load the cancellation catalogue. Cancellation rules are unavailable.</span>
      </Alert>
    );
  }

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6 max-w-xl"
      // Without this, a native HTML5 constraint (e.g. max="120") blocks submit before RHF
      // ever sees it, so the Zod field error above never shows.
      noValidate
    >
      <div>
        <Controller
          name="cancellations_allowed"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="cancellations_allowed"
              label="Cancellations Allowed"
              ariaLabel="Cancellations Allowed"
              ariaControls={CANCELLATION_FIELDS_REGION_ID}
              ariaExpanded={field.value}
              checked={field.value}
              onChange={field.onChange}
              disabled={controlsDisabled}
            />
          )}
        />
      </div>

      {enabled && formFieldMetas.length === 0 && (
        <Alert variant="info">
          <span>No cancellation fields are defined in the catalogue.</span>
        </Alert>
      )}

      {enabled && formFieldMetas.length > 0 && (
        <fieldset id={CANCELLATION_FIELDS_REGION_ID} className="rounded-lg border border-mp-border p-4">
          <legend className="px-2 text-sm font-medium">Cancellation rules</legend>
          <div className="space-y-4">
            {formFieldMetas.map((meta) => (
              <div key={meta.key} className="grid grid-cols-[1fr_auto] items-center gap-4">
                <Label htmlFor={`cancellation_${meta.key}`}>{meta.label}</Label>
                <Controller
                  name={`fields.${meta.key}` as const}
                  control={control}
                  render={({ field, fieldState }) => {
                    if (meta.control === 'checkbox') {
                      return (
                        <Checkbox
                          id={`cancellation_${meta.key}`}
                          ariaLabel={meta.label}
                          checked={Boolean(field.value)}
                          onChange={field.onChange}
                          disabled={controlsDisabled}
                        />
                      );
                    }
                    if (meta.control === 'number') {
                      return (
                        <Input
                          id={`cancellation_${meta.key}`}
                          type="number"
                          value={(field.value as string) ?? ''}
                          onChange={(e) => field.onChange(e.target.value)} // Input yields a DOM event
                          onBlur={field.onBlur}
                          min={meta.min !== undefined ? String(meta.min) : undefined} // min/max are STRINGS
                          max={meta.max !== undefined ? String(meta.max) : undefined}
                          disabled={controlsDisabled}
                          error={!!fieldState.error}
                          hint={fieldState.error?.message}
                        />
                      );
                    }
                    return (
                      <Select
                        id={`cancellation_${meta.key}`}
                        value={(field.value as string) ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        options={(meta.options ?? []).map((v) => ({ value: v, label: humanise(v) }))}
                        placeholder={meta.label} // Select derives its a11y name from placeholder
                        disabled={controlsDisabled}
                        error={!!fieldState.error}
                        hint={fieldState.error?.message}
                      />
                    );
                  }}
                />
              </div>
            ))}
            <DerivedAdjustmentTypes metas={derivedFieldMetas} derivation={adjustment} />
          </div>
        </fieldset>
      )}

      {saveMutation.isError && (
        <Alert variant="error">
          <span>{isApiError(saveMutation.error) ? saveMutation.error.message : 'Failed to save cancellation rules.'}</span>
        </Alert>
      )}

      {!isRetired && (
        <PermissionGuard permission={{ resource: 'campaign', action: 'edit' }}>
          <Button size="sm" disabled={isSubmitting || saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </PermissionGuard>
      )}
    </Form>
  );
}
